import { Component } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import {
  EndClientReportDataSource,
  EndClientReportDataSourceKind,
  EndClientReportDataSourcePreflight,
  EndClientReportStatus,
  endClientReportSourceLabel,
} from '../../models/end-client-reporting-package';
import { EndClientReportService } from './end-client-report.service';
import { DataSourceUploadModalComponent } from './data-source-upload-modal.component';

/**
 * Which data a deck reads. Per report, unlike the refresh dialog: refreshing re-pulls a source for
 * everything reading it, where this points one deck somewhere else and leaves the rest alone.
 *
 * Nothing is copied into the design when the source changes -- the deck resolves its variables
 * against whatever it reads -- so a question the new source doesn't have shows as "No data for ..."
 * on the block rather than as a zero.
 */
@Component({
  selector: 'app-data-source-modal',
  standalone: false,
  // The report's popup look -- see .ecr-modal in themes/end-client-reporting.scss.
  host: { class: 'ecr-modal' },
  template: `
    <div class="modal-header">
      <h5 class="modal-title nav-font16 font-weight-bold">Data source</h5>
      <button type="button" class="close" aria-label="Close" (click)="close()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <!-- Above the list rather than at its top, so it stays in view while the sources scroll: it is
         what choosing one means. -->
    @if (!loading && !loadFailed && sources.length) {
      <p class="source-intro grey5 nav-font14 mb-0">
        Changes what this report reads. Other reports are unaffected, and nothing about the
        report's layout changes — a question the new source doesn't have will say it has no data.
      </p>
    }

    <div class="modal-body">
      @if (loading) {
        <p class="grey5 nav-font14 mb-0">Loading data sources…</p>
      } @else {
        @if (loadFailed) {
          <p class="grey4 nav-font12">
            <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>Couldn't load the
            data sources for this client.
          </p>
        } @else if (!sources.length) {
          <p class="grey5 nav-font14">There are no other data sources for this client yet.</p>
        } @else {
          @for (source of sources; track source.id) {
            <div class="choice p-3 mb-2" [class.is-chosen]="chosenId === source.id"
                 [class.is-unusable]="unusable(source)">
              <input type="radio" [id]="'data-source-' + source.id" name="data-source"
                     [checked]="chosenId === source.id" [disabled]="unusable(source)"
                     (change)="choose(source)">
              <label class="mb-0 ml-2" [for]="'data-source-' + source.id">
                <span class="nav-font14 font-weight-bold">{{ source.name }}</span>
                @if (source.id === currentId) {
                  <span class="nav-font12 grey4 ml-2">Current</span>
                }
                <span class="d-block grey4 nav-font12">{{ describe(source) }}</span>
                @if (source.lastError) {
                  <span class="d-block nav-font12 source-error">{{ source.lastError }}</span>
                }
              </label>
            </div>
          }
        }

        <!-- Checked as soon as a different source is picked, not on confirm: the point is to inform
             the decision, and a warning that arrives with the outcome is just an explanation. -->
        @if (checking) {
          <p class="grey4 nav-font12">
            <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>Checking this report's
            questions against that data…
          </p>
        } @else if (preflight; as check) {
          @if (check.unknown) {
            <p class="grey4 nav-font12">
              That source has no survey structure yet, so what this report would show can't be
              checked. It will resolve once the data finishes loading.
            </p>
          } @else if (!check.missing.length) {
            <p class="grey4 nav-font12">
              <i class="fas fa-check mr-1" aria-hidden="true"></i>All
              {{ check.variables }} of this report's questions exist in that data.
            </p>
          } @else {
            <div class="preflight-warning p-3 mb-2">
              <p class="nav-font12 mb-1 font-weight-bold">
                {{ check.missing.length }} of this report's {{ check.variables }} questions
                {{ check.missing.length === 1 ? "isn't" : "aren't" }} in that data.
              </p>
              <p class="grey5 nav-font12 mb-1">
                Charts using {{ check.missing.length === 1 ? 'it' : 'them' }} will say they have no
                data. Nothing else about the report changes, and switching back restores them.
              </p>
              <span class="grey4 nav-font12">{{ check.missing.join(', ') }}</span>
            </div>
          }
        }
      }
    </div>

    <!-- Under the list rather than at the end of it, so it is read whatever the list's length:
         it explains why the list is shorter than expected. -->
    @if (!loading && hiddenCount) {
      <p class="hidden-note grey4 nav-font12 mb-0">
        {{ hiddenCount }} data source{{ hiddenCount === 1 ? '' : 's' }} couldn't be read and
        {{ hiddenCount === 1 ? "isn't" : "aren't" }} shown here. Fix
        {{ hiddenCount === 1 ? 'it' : 'them' }} from the Data Sources tab.
      </p>
    }

    <div class="modal-footer">
      <!-- In the footer, not at the end of the list: the body scrolls once there are enough
           sources, and the way to add one should not be at the bottom of the scroll. -->
      @if (!loading) {
        <button type="button" class="btn btn-link p-0 nav-font12 mr-auto" (click)="startUpload()">
          <i class="fas fa-upload mr-1" aria-hidden="true"></i>Upload a data source
        </button>
      }
      <button type="button" class="btn btn-sm btn-link grey5" (click)="close()">Cancel</button>
      <!-- Held while the compatibility check runs: it exists to inform this decision, and a
           consequence that lands after the click is just an explanation. -->
      <button type="button" class="btn btn-sm btn-primary"
              [disabled]="loading || checking || !chosenId || chosenId === currentId"
              (click)="confirm()">
        @if (checking) {
          <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>Checking…
        } @else {
          <span>Use this source</span>
        }
      </button>
    </div>
  `,
  styles: [`
    /* Between the scrolling list and the buttons, at the body's own inset, and never squeezed by
       the column the popup lays out in. */
    .hidden-note {
      flex-shrink: 0;
      padding: 10px 22px 0;
    }
    /* Above the scrolling list, at the body's inset, with the room the body's own top padding gave
       it before. */
    .source-intro {
      flex-shrink: 0;
      padding: 6px 22px 12px;
    }
    .choice {
      display: flex;
      align-items: flex-start;
      border: 1px solid var(--nav-grey1);
      border-radius: 6px;
      cursor: pointer;
    }
    .choice.is-chosen {
      border-color: var(--nav-secondary);
      background: var(--nav-grey0, #f7f7f7);
    }
    .choice label {
      cursor: pointer;
    }
    .choice.is-unusable {
      opacity: 0.65;
      cursor: default;
    }
    .choice.is-unusable label {
      cursor: default;
    }
    .source-error {
      color: var(--nav-error);
    }
    /* Bordered rather than red text: this is a consequence to read, not an error to fix. */
    .preflight-warning {
      border: 1px solid var(--nav-warning, #e0a800);
      border-radius: 6px;
      background: var(--nav-grey0, #f7f7f7);
    }
  `]
})
export class DataSourceModalComponent {
  loading = true;
  loadFailed = false;
  sources: EndClientReportDataSource[] = [];

  /** Sources left out because there is nothing to read in them — said out loud, not silently dropped. */
  hiddenCount = 0;

  /** The source the report reads now, so it can be marked and the confirm button left inert. */
  currentId?: string;
  chosenId?: string;

  /** What picking the currently-chosen source would cost this deck. Null until one is picked. */
  preflight: EndClientReportDataSourcePreflight | null = null;
  checking = false;

  /** Set by the opener; called with the chosen source when confirmed. */
  onConfirm?: (dataSourceId: string) => void;

  private projectId = '';
  private reportId = '';

  constructor(
    private modal: BsModalRef,
    private modalService: BsModalService,
    private reportService: EndClientReportService,
  ) { }

  /** Opened before its list exists, so the dialog appears at once and fills in behind. */
  load(reportId: string, projectId: string, currentId?: string) {
    this.reportId = reportId;
    this.projectId = projectId;
    this.currentId = currentId;
    this.chosenId = currentId;
    this.preflight = null;
    this.loading = true;
    this.loadFailed = false;

    this.reportService.getDataSources(projectId).subscribe({
      next: sources => {
        // A source with nothing to read is noise here: choosing it would only empty the deck, and
        // this dialog can't fix it. They stay listed on the Data Sources tab, which can.
        //
        // The one this deck already reads is kept whatever state it is in -- filtering out the
        // current source would leave the picker unable to say what it is, and a project whose first
        // pull hasn't landed yet would open an empty dialog.
        const usable = sources.filter(s => !this.unusable(s) || s.id === this.currentId);
        this.hiddenCount = sources.length - usable.length;
        this.sources = usable;
        this.loading = false;
      },
      error: () => {
        this.sources = [];
        this.hiddenCount = 0;
        this.loadFailed = true;
        this.loading = false;
      }
    });
  }

  /**
   * Picking a source runs the check against it. Only for a different one -- asking what the deck
   * would lose by reading what it already reads is noise.
   */
  choose(source: EndClientReportDataSource) {
    this.chosenId = source.id;
    this.preflight = null;

    if (source.id === this.currentId) return;

    this.checking = true;
    this.reportService.preflightDataSource(this.reportId, source.id).subscribe({
      next: check => {
        // Ignore an answer for a source the person has since moved off.
        if (this.chosenId === source.id) this.preflight = check;
        this.checking = false;
      },
      // Silent: the switch is still allowed, and a failed check says nothing about compatibility.
      error: () => this.checking = false
    });
  }

  describe(source: EndClientReportDataSource): string {
    const kindLabel = endClientReportSourceLabel(source);
    // Another project's data is only here because it was shared, and that's worth knowing before
    // pointing a deck at it: a refresh from there changes this report too.
    const kind = source.ownerProjectId && source.ownerProjectId !== this.projectId
      ? `${kindLabel} · shared by another project`
      : kindLabel;

    // A failed source used to read "no data pulled yet" -- the same words as one that simply hasn't
    // run, which is how a bad upload came back saying nothing at all. The two are now distinct, and
    // a failure that still has older data says so rather than implying the deck is empty.
    //
    // A survey source is listed before its first pull, and EnsureForProject writes that row as
    // Failed with no error -- so only a recorded error reads as a failure.
    if (source.status === EndClientReportStatus.Failed) {
      if (source.dataFetchedAt) {
        return `${kind} · last attempt failed, still showing data from ${new Date(source.dataFetchedAt).toLocaleDateString()}`;
      }
      if (source.kind === EndClientReportDataSourceKind.DecipherProject) {
        return source.lastError ? `${kind} · last pull failed, choosing it tries again` : `${kind} · not pulled yet`;
      }
      return `${kind} · couldn't be read`;
    }
    if (source.status === EndClientReportStatus.Ingesting) {
      return source.dataFetchedAt ? `${kind} · updating…` : `${kind} · preparing…`;
    }
    return source.dataFetchedAt
      ? `${kind} · last updated ${new Date(source.dataFetchedAt).toLocaleDateString()}`
      : `${kind} · no data yet`;
  }

  /**
   * An upload with nothing to read and nothing to retry it, so pointing a deck at it would only
   * produce empty charts. A failure that still has older data stays selectable -- stale numbers beat
   * none, and the row says which it is -- and so does a survey with none, since switching to one
   * starts its pull.
   */
  unusable(source: EndClientReportDataSource): boolean {
    return source.kind !== EndClientReportDataSourceKind.DecipherProject
      && source.status === EndClientReportStatus.Failed && !source.dataFetchedAt;
  }

  /**
   * The upload form is its own dialog, shared with the data-sources section. Stacked over this one
   * rather than replacing it: the list is the thing being added to, and it is what the person
   * returns to.
   */
  startUpload() {
    const uploader = this.modalService.show(DataSourceUploadModalComponent, { class: 'modal-md' }).content;
    if (!uploader) return;
    uploader.configureCreate(this.projectId);
    uploader.onUploaded = source => {
      this.load(this.reportId, this.projectId, this.currentId);
      this.chosenId = source.id;
    };
  }

  confirm() {
    if (!this.chosenId || this.chosenId === this.currentId) return;
    this.onConfirm?.(this.chosenId);
    this.close();
  }

  close() {
    this.modal.hide();
  }
}
